<script>
import StopWatch from './StopWatch.vue'
import QrScanner from './QrScanner.vue'

export default {
  data() {
    return {
      tasks: [],
      emergencyName: '',
      personInCharge: '',
      editingEmergencyName: false,
      editingPersonInCharge: false,
      new_task: '',
      nextTaskId: 1,
      nextPersonId: 1,
      defaultBoards: [],
      defaultStation: '',
      timerConfig: {
        maxTime: 40 * 60 * 1000, // 40 minutes in ms
        colorThresholds: [
          { time: 15 * 60 * 1000, color: '#2dce89' }, // 0-15 min: green
          { time: 25 * 60 * 1000, color: '#ffd600' }, // 15-25 min: yellow
          { time: Infinity, color: '#ed4245' }, // 25+ min: red
        ],
      },
      movingPersonState: {
        personId: null,
        taskId: null,
        wasPaused: null,
      },
      selectedPersons: new Set(),
      showQrScanner: false,
    }
  },
  watch: {
    tasks: {
      handler() {
        this.saveState()
      },
      deep: true,
    },
    emergencyName() {
      this.saveState()
    },
    personInCharge() {
      this.saveState()
    },
  },
  created() {
    console.log('TaskList created, loading state')
    this.loadState()
  },
  methods: {
    saveState() {
      const state = {
        tasks: this.tasks,
        emergencyName: this.emergencyName,
        personInCharge: this.personInCharge,
        nextTaskId: this.nextTaskId,
        nextPersonId: this.nextPersonId,
      }
      localStorage.setItem('nexo-vue-tasks-state', JSON.stringify(state))
      this.savePersonsData() // Save current person data for ActivityLog
    },

    // Activity logging methods
    logActivity(activity) {
      const activities = this.getActivities()
      const newActivity = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...activity,
      }
      activities.push(newActivity)
      localStorage.setItem('nexo-vue-activities', JSON.stringify(activities))
    },

    getActivities() {
      const saved = localStorage.getItem('nexo-vue-activities')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Error loading activities:', e)
          return []
        }
      }
      return []
    },

    clearActivities() {
      localStorage.removeItem('nexo-vue-activities')
    },

    // Save current person data for ActivityLog to access
    savePersonsData() {
      const personsData = {}
      this.tasks.forEach((task) => {
        task.persons.forEach((person) => {
          personsData[person.id] = {
            id: person.id,
            name: person.name,
            role: person.role,
            station: person.station,
            taskName: task.name,
          }
        })
      })
      localStorage.setItem('nexo-vue-persons-data', JSON.stringify(personsData))
    },
    saveDefaultBoards() {
      localStorage.setItem(
        'nexo-vue-default-boards',
        JSON.stringify(this.defaultBoards)
      )
    },
    saveDefaultStation() {
      localStorage.setItem('nexo-vue-default-station', this.defaultStation)
    },
    loadDefaultStation() {
      const savedDefaultStation = localStorage.getItem(
        'nexo-vue-default-station'
      )
      if (savedDefaultStation) {
        this.defaultStation = savedDefaultStation
      }
    },
    saveTimerConfig() {
      localStorage.setItem(
        'nexo-vue-timer-config',
        JSON.stringify(this.timerConfig)
      )
    },
    loadTimerConfig() {
      const savedTimerConfig = localStorage.getItem('nexo-vue-timer-config')
      if (savedTimerConfig) {
        this.timerConfig = JSON.parse(savedTimerConfig)
      }
    },
    loadDefaultBoards() {
      const savedDefaults = localStorage.getItem('nexo-vue-default-boards')
      if (savedDefaults) {
        this.defaultBoards = JSON.parse(savedDefaults)
      }
    },
    createDefaultBoardsInstance() {
      return this.defaultBoards.map((board) => ({
        ...board,
        id: board.isPreset ? 0 : this.nextTaskId++,
        edit: false,
        new_person: '',
        new_role: '',
        new_station: this.defaultStation,
        persons: [],
        multiSelectMode: false,
      }))
    },
    loadState() {
      this.loadDefaultBoards()
      this.loadDefaultStation()
      this.loadTimerConfig()
      const savedState = localStorage.getItem('nexo-vue-tasks-state')
      if (savedState) {
        const state = JSON.parse(savedState)
        this.tasks = state.tasks
        this.emergencyName = state.emergencyName || ''
        this.personInCharge = state.personInCharge || ''
        this.nextTaskId = state.nextTaskId || 1
        this.nextPersonId = state.nextPersonId || 1
      } else {
        // Initialize with default boards if nothing is saved
        if (this.defaultBoards.length === 0) {
          // Create the default "Personal disponible" if no default boards exist
          this.defaultBoards = [
            {
              name: 'Personal disponible',
              isPreset: true,
              isDefault: true,
            },
          ]
          this.saveDefaultBoards()
        }
        this.tasks = this.createDefaultBoardsInstance()
      }
    },
    startDrag(event, item) {
      event.dataTransfer.dropEffect = 'move'
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('itemID', item.id)
      event.dataTransfer.setData('listID', item.task_id)

      // "Pause" the timer by committing the elapsed time as soon as drag starts.
      if (!item.timer.isPaused) {
        const elapsed = Date.now() - item.timer.startTime
        item.timer = {
          ...item.timer,
          totalElapsed: item.timer.totalElapsed + elapsed,
          isPaused: true,
        }
      }
    },
    onDrop(event, list) {
      const itemID = parseInt(event.dataTransfer.getData('itemID'), 10)
      const listID = parseInt(event.dataTransfer.getData('listID'), 10)

      const origin_task = this.tasks.find((task) => task.id === listID)
      if (!origin_task) return
      const personIndex = origin_task.persons.findIndex((p) => p.id === itemID)
      if (personIndex === -1) return

      const [person] = origin_task.persons.splice(personIndex, 1)

      // "Resume" the timer by resetting the start time and unpausing.
      person.timer = {
        ...person.timer,
        isPaused: false,
        startTime: Date.now(),
      }
      person.task_id = list.id

      list.persons.push(person)

      // Log activity directly to localStorage
      this.logActivity({
        type: 'person',
        subtype: 'moved',
        description: `Persona "${person.name || 'Sin nombre'}" movida de "${
          origin_task.name || 'Sin origen'
        }" a "${list.name || 'Sin destino'}"`,
        details: {
          personId: person.id,
          personName: person.name || 'Sin nombre',
          fromTask: origin_task.name || 'Sin origen',
          toTask: list.name || 'Sin destino',
        },
      })
    },
    toggleTimer(person) {
      if (person.timer.isPaused) {
        // Resume
        person.timer = {
          ...person.timer,
          isPaused: false,
          startTime: Date.now(),
        }
      } else {
        // Pause
        const elapsed = Date.now() - person.timer.startTime
        person.timer = {
          ...person.timer,
          totalElapsed: person.timer.totalElapsed + elapsed,
          isPaused: true,
        }
      }
    },
    stopTimer(person) {
      person.timer.totalElapsed = 0
      person.timer.isPaused = true
      person.timer.startTime = Date.now() // Reset start time
    },
    startEditTask(task) {
      task.originalName = task.name
      task.edit = true
      this.$nextTick(() => {
        this.focusNext('editTaskNameInput_' + task.id)
      })
    },
    saveTask(task) {
      task.edit = false
      delete task.originalName
    },
    cancelEditTask(task) {
      task.name = task.originalName
      task.edit = false
      delete task.originalName
    },
    startEditPerson(person) {
      person.originalName = person.name
      person.originalRole = person.role
      person.originalStation = person.station
      person.edit = true
      this.$nextTick(() => {
        this.focusNext('editPersonNameInput_' + person.id)
      })
    },
    savePerson(person) {
      // Check what changed
      const changes = []
      if (person.originalName !== person.name) {
        changes.push(`Nombre: ${person.originalName} → ${person.name}`)
      }
      if (person.originalRole !== person.role) {
        changes.push(
          `Rol: ${person.originalRole || 'Sin rol'} → ${
            person.role || 'Sin rol'
          }`
        )
      }
      if (person.originalStation !== person.station) {
        changes.push(
          `Estación: ${person.originalStation || 'Sin estación'} → ${
            person.station || 'Sin estación'
          }`
        )
      }

      // Find task for this person
      const task = this.tasks.find((t) =>
        t.persons.some((p) => p.id === person.id)
      )

      // Log activity if there were changes
      if (changes.length > 0 && task) {
        this.logActivity({
          type: 'person',
          subtype: 'updated',
          description: `Persona "${
            person.name || 'Sin nombre'
          }" actualizada en "${task.name || 'Sin tarea'}"`,
          details: {
            personId: person.id,
            personName: person.name || 'Sin nombre',
            taskName: task.name || 'Sin tarea',
            changes: changes.join(', ') || 'Sin cambios especificados',
          },
        })
      }

      person.edit = false
      delete person.originalName
      delete person.originalRole
      delete person.originalStation
    },
    cancelEditPerson(person) {
      person.name = person.originalName
      person.role = person.originalRole
      person.station = person.originalStation
      person.edit = false
      delete person.originalName
      delete person.originalRole
      delete person.originalStation
    },
    deleteItem(taskId, itemId) {
      const task = this.tasks.find((task) => task.id === taskId)
      if (task) {
        task.persons = task.persons.filter((person) => person.id !== itemId)
      }
    },
    deleteTask(task) {
      // Check if the board has any persons
      if (task.persons && task.persons.length > 0) {
        alert(
          `No se puede eliminar el tablero "${task.name}" porque contiene personas. Para eliminarlo, primero mueva todas las personas a otros tableros.`
        )
        return
      }

      if (
        confirm(
          `¿Está seguro de que desea eliminar el tablero "${task.name}"? Esta acción no se puede deshacer.`
        )
      ) {
        // Remove from tasks array
        const taskIndex = this.tasks.findIndex((t) => t.id === task.id)
        if (taskIndex !== -1) {
          this.tasks.splice(taskIndex, 1)
        }

        // If it's a default board, also remove from default boards
        if (task.isDefault) {
          this.removeDefaultBoard(task.name)
        }
      }
    },
    availableDestinations(currentTaskId) {
      return this.tasks.filter((task) => task.id !== currentTaskId)
    },
    startMovePerson(person) {
      // Store the person's state before initiating the move
      this.movingPersonState = {
        personId: person.id,
        taskId: person.task_id,
        wasPaused: person.timer.isPaused,
      }

      // Pause the timer if it's running, just like when starting a drag
      if (!person.timer.isPaused) {
        const elapsed = Date.now() - person.timer.startTime
        person.timer = {
          ...person.timer,
          totalElapsed: person.timer.totalElapsed + elapsed,
          isPaused: true,
        }
      }
    },
    executeMove(personToMove, destinationTask) {
      const origin_task = this.tasks.find(
        (task) => task.id === personToMove.task_id
      )
      if (!origin_task) return

      const personIndex = origin_task.persons.findIndex(
        (p) => p.id === personToMove.id
      )
      if (personIndex === -1) return

      // Remove from old list
      const [person] = origin_task.persons.splice(personIndex, 1)

      // "Resume" the timer by resetting the start time and unpausing, just like on drop
      person.timer = {
        ...person.timer,
        isPaused: false,
        startTime: Date.now(),
      }
      person.task_id = destinationTask.id

      // Add to new list
      destinationTask.persons.push(person)

      this.resetMovingPersonState()
    },
    cancelMovePerson(person) {
      // If the timer was running before, resume it
      if (this.movingPersonState.wasPaused === false) {
        this.toggleTimer(person)
      }
      this.resetMovingPersonState()
    },
    resetMovingPersonState() {
      this.movingPersonState = { personId: null, taskId: null, wasPaused: null }
    },
    addPerson(task) {
      if (!task.new_person || !task.new_person.trim()) return

      const newPerson = {
        id: this.nextPersonId++,
        name: task.new_person,
        role: task.new_role,
        station: task.new_station,
        task_id: task.id,
        edit: false,
        // Initialize timer state for the new person
        timer: {
          startTime: Date.now(),
          totalElapsed: 0,
          isPaused: true,
        },
      }

      task.persons.push(newPerson)

      // Log activity directly to localStorage
      this.logActivity({
        type: 'person',
        subtype: 'created',
        description: `Persona "${newPerson.name || 'Sin nombre'}" añadida a "${
          task.name || 'Sin tarea'
        }"`,
        details: {
          personId: newPerson.id,
          personName: newPerson.name || 'Sin nombre',
          taskName: task.name || 'Sin tarea',
          role: newPerson.role || 'Sin rol',
          station: newPerson.station || 'Sin estación',
        },
      })

      task.new_person = ''
      task.new_role = ''
      // Reset station to default value instead of empty string
      task.new_station = this.defaultStation

      // After adding, focus the first input for the next entry.
      // Use $nextTick to wait for the DOM to update.
      this.$nextTick(() => {
        this.focusNext('newPersonInput')
      })
    },
    addScannedPerson({ name, station }) {
      const defaultTask = this.tasks.find((t) => t.id === 0)
      if (!defaultTask) return

      const newPerson = {
        id: this.nextPersonId++,
        name: name,
        role: '',
        station: station,
        task_id: 0,
        edit: false,
        timer: {
          startTime: Date.now(),
          totalElapsed: 0,
          isPaused: true,
        },
      }

      defaultTask.persons.push(newPerson)

      this.logActivity({
        type: 'person',
        subtype: 'created',
        description: `Persona "${newPerson.name}" añadida a "${defaultTask.name}" (QR)`,
        details: {
          personId: newPerson.id,
          personName: newPerson.name,
          taskName: defaultTask.name,
          role: '',
          station: newPerson.station || 'Sin estación',
        },
      })
    },
    focusNext(refName) {
      const el = this.$refs[refName]
      if (el) {
        const targetEl = Array.isArray(el) ? el[0] : el
        targetEl?.focus()
      }
    },
    addTask() {
      if (!this.new_task || !this.new_task.trim()) return
      this.tasks.push({
        id: this.nextTaskId++,
        name: this.new_task,
        edit: false,
        new_person: '',
        new_role: '',
        new_station: this.defaultStation,
        persons: [],
        multiSelectMode: false,
      })
      this.new_task = ''
    },
    addDefaultBoard(boardData) {
      const newBoard = {
        name: boardData.name,
        isDefault: true,
        isPreset: false,
      }
      this.defaultBoards.push(newBoard)
      this.saveDefaultBoards()

      // Add the board to current tasks as well
      this.tasks.push({
        ...newBoard,
        id: this.nextTaskId++,
        edit: false,
        new_person: '',
        new_role: '',
        new_station: this.defaultStation,
        persons: [],
        multiSelectMode: false,
      })
    },
    removeDefaultBoard(boardName) {
      // Remove from default boards list
      const boardIndex = this.defaultBoards.findIndex(
        (board) => board.name === boardName
      )
      if (boardIndex !== -1) {
        this.defaultBoards.splice(boardIndex, 1)
        this.saveDefaultBoards()

        // Find the corresponding task and mark it as non-default
        const taskIndex = this.tasks.findIndex(
          (task) => task.name === boardName
        )
        if (taskIndex !== -1) {
          // Remove default properties, making it a regular task
          this.tasks[taskIndex].isDefault = false
          this.tasks[taskIndex].isPreset = false
        }
      }
    },
    getDefaultBoards() {
      return this.defaultBoards
    },
    getDefaultStation() {
      return this.defaultStation
    },
    getTimerConfig() {
      return this.timerConfig
    },
    setTimerConfig(config) {
      this.timerConfig = { ...config }
      this.saveTimerConfig()
    },
    setDefaultStation(station) {
      this.defaultStation = station
      this.saveDefaultStation()

      // Update all existing tasks to use the new default station
      this.tasks.forEach((task) => {
        task.new_station = this.defaultStation
      })
    },
    clearDefaultStation() {
      this.defaultStation = ''
      this.saveDefaultStation()

      // Update all existing tasks to clear the station field
      this.tasks.forEach((task) => {
        task.new_station = ''
      })
    },
    updateDefaultBoard(oldName, newName) {
      const boardIndex = this.defaultBoards.findIndex(
        (board) => board.name === oldName
      )
      if (boardIndex !== -1) {
        this.defaultBoards[boardIndex].name = newName
        this.saveDefaultBoards()

        // Also update the name in current tasks
        const taskIndex = this.tasks.findIndex((task) => task.name === oldName)
        if (taskIndex !== -1) {
          this.tasks[taskIndex].name = newName
        }
      }
    },
    resetApp() {
      // Clear all localStorage data
      localStorage.removeItem('nexo-vue-tasks-state')
      localStorage.removeItem('nexo-vue-default-boards')
      localStorage.removeItem('nexo-vue-default-station')
      localStorage.removeItem('nexo-vue-timer-config')

      // Reset to initial state with only "Personal disponible"
      this.defaultBoards = [
        {
          name: 'Personal disponible',
          isPreset: true,
          isDefault: true,
        },
      ]
      this.saveDefaultBoards()

      // Reset default station
      this.defaultStation = ''

      // Reset emergency info
      this.emergencyName = ''
      this.personInCharge = ''

      // Reset timer config to defaults
      this.timerConfig = {
        maxTime: 40 * 60 * 1000, // 40 minutes in ms
        colorThresholds: [
          { time: 15 * 60 * 1000, color: '#2dce89' }, // 0-15 min: green
          { time: 25 * 60 * 1000, color: '#ffd600' }, // 15-25 min: yellow
          { time: Infinity, color: '#ed4245' }, // 25+ min: red
        ],
      }

      // Reset tasks to only default boards
      this.tasks = this.createDefaultBoardsInstance()

      // Reset counters
      this.nextTaskId = 1
      this.nextPersonId = 1

      // Save the reset state
      this.saveState()
    },
    clearAll() {
      if (
        confirm(
          '¿Está seguro de que desea eliminar todos los tableros, tareas y registros? Esta acción no se puede deshacer.'
        )
      ) {
        // Reset to default boards only
        this.tasks = this.createDefaultBoardsInstance()
        // Clear emergency info
        this.emergencyName = ''
        this.personInCharge = ''
        // Reset IDs but account for default boards
        const maxDefaultId = Math.max(
          ...this.defaultBoards.map((board) => (board.isPreset ? 0 : -1))
        )
        this.nextTaskId = Math.max(maxDefaultId + 1, 1)
        this.nextPersonId = 1

        // Clear activity log
        this.clearActivities()

        // Save the reset state
        this.saveState()
      }
    },
    // Multi-select methods
    toggleMultiSelectMode(task) {
      // Toggle multi-select mode for this specific task
      task.multiSelectMode = !task.multiSelectMode
      if (!task.multiSelectMode) {
        // Clear selections for this task when disabling multi-select
        task.persons.forEach((person) => {
          this.selectedPersons.delete(person.id)
        })
      }
    },
    isTaskInMultiSelectMode(task) {
      return task.multiSelectMode || false
    },
    togglePersonSelection(person) {
      if (this.selectedPersons.has(person.id)) {
        this.selectedPersons.delete(person.id)
      } else {
        this.selectedPersons.add(person.id)
      }
    },
    isPersonSelected(person) {
      return this.selectedPersons.has(person.id)
    },
    getSelectedPersonsFromTask(taskId) {
      const task = this.tasks.find((t) => t.id === taskId)
      if (!task) return []
      return task.persons.filter((person) =>
        this.selectedPersons.has(person.id)
      )
    },
    hasSelectedPersons(taskId) {
      return this.getSelectedPersonsFromTask(taskId).length > 0
    },
    moveSelectedPersons(sourceTaskId, destinationTask) {
      const sourceTask = this.tasks.find((t) => t.id === sourceTaskId)
      if (!sourceTask || !destinationTask) return

      const selectedPersonsInTask =
        this.getSelectedPersonsFromTask(sourceTaskId)
      if (selectedPersonsInTask.length === 0) return

      // Pause timers for selected persons before moving
      selectedPersonsInTask.forEach((person) => {
        if (!person.timer.isPaused) {
          const elapsed = Date.now() - person.timer.startTime
          person.timer = {
            ...person.timer,
            totalElapsed: person.timer.totalElapsed + elapsed,
            isPaused: true,
          }
        }
      })

      // Remove selected persons from source task
      sourceTask.persons = sourceTask.persons.filter(
        (person) => !this.selectedPersons.has(person.id)
      )

      // Add selected persons to destination task and resume timers
      selectedPersonsInTask.forEach((person) => {
        person.timer = {
          ...person.timer,
          isPaused: false,
          startTime: Date.now(),
        }
        person.task_id = destinationTask.id
        destinationTask.persons.push(person)
        this.selectedPersons.delete(person.id)
      })

      // Turn off multi-select mode after moving
      sourceTask.multiSelectMode = false
    },

    // Emergency info editing methods
    startEditEmergencyName() {
      this.editingEmergencyName = true
      this.$nextTick(() => {
        this.$refs.emergencyNameInput?.focus()
      })
    },

    stopEditEmergencyName() {
      this.editingEmergencyName = false
    },

    startEditPersonInCharge() {
      this.editingPersonInCharge = true
      this.$nextTick(() => {
        this.$refs.personInChargeInput?.focus()
      })
    },

    stopEditPersonInCharge() {
      this.editingPersonInCharge = false
    },
  },
  components: { StopWatch, QrScanner },
}
</script>

<template>
  <div class="contabilidad-container">
    <div class="contabilidad-header">
      <h2>Sistema de Contabilidad</h2>
      <p class="contabilidad-description">
        Gestión de tareas y control de tiempos del personal
      </p>
    </div>

    <div class="emergency-info">
      <div class="emergency-field">
        <span class="emergency-label">Nombre de la Emergencia:</span>
        <span
          v-if="!editingEmergencyName"
          @click="startEditEmergencyName"
          class="emergency-value"
          :class="{ placeholder: !emergencyName }"
        >
          {{
            emergencyName || 'Hacer clic para añadir nombre de emergencia...'
          }}
        </span>
        <input
          v-if="editingEmergencyName"
          ref="emergencyNameInput"
          v-model="emergencyName"
          @blur="stopEditEmergencyName"
          @keyup.enter="stopEditEmergencyName"
          @keyup.esc="stopEditEmergencyName"
          class="emergency-input-inline"
          placeholder="Ingrese el nombre de la emergencia..."
        />
      </div>
      <div class="emergency-field">
        <span class="emergency-label">Oficial o bombero a cargo:</span>
        <span
          v-if="!editingPersonInCharge"
          @click="startEditPersonInCharge"
          class="emergency-value"
          :class="{ placeholder: !personInCharge }"
        >
          {{ personInCharge || 'Hacer clic para añadir responsable...' }}
        </span>
        <input
          v-if="editingPersonInCharge"
          ref="personInChargeInput"
          v-model="personInCharge"
          @blur="stopEditPersonInCharge"
          @keyup.enter="stopEditPersonInCharge"
          @keyup.esc="stopEditPersonInCharge"
          class="emergency-input-inline"
          placeholder="Ingrese el nombre del responsable..."
        />
      </div>
    </div>

    <div class="boards-container">
      <ul
        class="drop-zone"
        v-for="task in tasks"
        :key="task.id"
        @drop="onDrop($event, task)"
        @dragover.prevent
        @dragenter.prevent
      >
        <div class="board-header">
          <div v-if="!task.edit" class="task-display">
            <div class="board-title-container">
              <b
                >{{ task.name
                }}{{
                  task.persons.length > 0
                    ? ` (${task.persons.length} persona${
                        task.persons.length !== 1 ? 's' : ''
                      })`
                    : ''
                }}</b
              >
              <div class="board-actions">
                <button
                  @click="toggleMultiSelectMode(task)"
                  :class="{ active: isTaskInMultiSelectMode(task) }"
                  title="Modo de selección múltiple"
                  class="edit-btn multi-select-btn"
                >
                  <span class="material-icons">checklist</span>
                </button>
                <button
                  v-if="!task.isDefault"
                  @click="startEditTask(task)"
                  title="Editar nombre del tablero"
                  class="edit-btn board-edit-btn"
                >
                  <span class="material-icons">edit</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Multi-select controls -->
          <div
            v-if="isTaskInMultiSelectMode(task) && !task.edit"
            class="multi-select-controls"
          >
            <!-- Move selected persons controls -->
            <div
              v-if="hasSelectedPersons(task.id)"
              class="move-selected-controls"
            >
              <h4 class="move-title">Mover seleccionados</h4>
              <div class="move-options">
                <div
                  v-for="destination in availableDestinations(task.id)"
                  :key="destination.id"
                  class="move-option"
                >
                  <button
                    @click="moveSelectedPersons(task.id, destination)"
                    class="move-destination-btn"
                  >
                    {{ destination.name }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <form v-if="task.edit" @submit.prevent="saveTask(task)">
            <input
              :ref="'editTaskNameInput_' + task.id"
              v-model="task.name"
              @keyup.enter.exact="saveTask(task)"
              placeholder="Nombre de la zona"
            />
            <div class="form-controls">
              <button type="submit" class="primary">Guardar</button>
              <button
                type="button"
                @click="cancelEditTask(task)"
                class="secondary"
              >
                Cancelar
              </button>
              <button type="button" @click="deleteTask(task)" class="danger">
                Eliminar
              </button>
            </div>
          </form>
        </div>

        <li
          class="drag-el"
          v-for="person in task.persons"
          :key="person.id"
          :draggable="!isTaskInMultiSelectMode(task)"
          @dragstart="startDrag($event, person)"
          :class="{
            selected: isTaskInMultiSelectMode(task) && isPersonSelected(person),
            'multi-select-mode': isTaskInMultiSelectMode(task),
          }"
        >
          <!-- Default Card View -->
          <div v-if="movingPersonState.personId !== person.id">
            <div class="card-content">
              <div class="card-header">
                <div class="person-info">
                  <input
                    v-if="isTaskInMultiSelectMode(task)"
                    type="checkbox"
                    :checked="isPersonSelected(person)"
                    @change="togglePersonSelection(person)"
                    class="person-checkbox"
                  />
                  <span v-if="person.station !== ''"
                    >{{ person.name }} ({{ person.station }})</span
                  >
                  <span v-else>{{ person.name }}</span>
                </div>
                <div class="card-actions" v-if="!isTaskInMultiSelectMode(task)">
                  <button
                    v-if="!person.edit"
                    @click="startMovePerson(person)"
                    title="Mover"
                    class="edit-btn"
                  >
                    <span class="material-icons">double_arrow</span>
                  </button>
                  <button
                    v-if="!person.edit"
                    @click="startEditPerson(person)"
                    title="Edit"
                    class="edit-btn"
                  >
                    <span class="material-icons">edit</span>
                  </button>
                </div>
              </div>

              <p v-if="person.role && !person.edit" class="person-function">
                Función: {{ person.role }}
              </p>
            </div>
            <form
              v-if="person.edit"
              @submit.prevent="savePerson(person)"
              class="edit-person-form"
            >
              <input
                :ref="'editPersonNameInput_' + person.id"
                v-model="person.name"
                placeholder="Nombre"
                @keyup.enter.exact="
                  focusNext('editPersonStationInput_' + person.id)
                "
              />
              <input
                :ref="'editPersonStationInput_' + person.id"
                v-model="person.station"
                placeholder="Compañía"
                @keyup.enter.exact="
                  focusNext('editPersonRoleInput_' + person.id)
                "
              />
              <input
                :ref="'editPersonRoleInput_' + person.id"
                v-model="person.role"
                placeholder="Función"
              />
              <div class="form-controls">
                <button type="submit" class="primary">Guardar</button>
                <button
                  type="button"
                  @click="cancelEditPerson(person)"
                  class="secondary"
                >
                  Cancelar
                </button>
                <button @click="deleteItem(task.id, person.id)" class="danger">
                  Eliminar
                </button>
              </div>
            </form>
            <StopWatch
              :key="person.id"
              :total-elapsed="person.timer.totalElapsed"
              :start-time="person.timer.startTime"
              :is-paused="person.timer.isPaused"
              :progress-bar-config="timerConfig"
              @toggle-pause="toggleTimer(person)"
              @stop="stopTimer(person)"
            />
          </div>

          <!-- View for Moving a Person -->
          <div
            v-if="movingPersonState.personId === person.id"
            class="move-person-form"
          >
            <p class="move-title">Mover a:</p>
            <button
              v-for="dest in availableDestinations(task.id)"
              :key="dest.id"
              @click="executeMove(person, dest)"
              class="secondary full-width move-option"
            >
              {{ dest.name }}
            </button>
            <button
              @click="cancelMovePerson(person)"
              class="secondary full-width move-option"
            >
              Cancelar
            </button>
          </div>
        </li>

        <!-- The form must be wrapped in an <li> to be a valid child of <ul> -->
        <li v-if="task.id == 0">
          <form @submit.prevent="addPerson(task)" class="add-person-form">
            <input
              ref="newPersonInput"
              v-model="task.new_person"
              placeholder="Nombre"
              @keydown.enter.prevent="focusNext('newStationInput')"
            />
            <input
              ref="newStationInput"
              v-model="task.new_station"
              placeholder="Compañía"
              @keydown.enter.prevent="focusNext('newRoleInput')"
            />
            <input
              ref="newRoleInput"
              v-model="task.new_role"
              placeholder="Función"
              @keydown.enter.prevent="addPerson(task)"
            />
            <button type="submit" class="primary full-width">
              Agregar Persona
            </button>
            <button
              type="button"
              class="secondary full-width"
              style="margin-top: 8px"
              @click="showQrScanner = true"
            >
              <span
                class="material-icons"
                style="
                  font-size: 16px;
                  vertical-align: middle;
                  margin-right: 4px;
                "
                >qr_code_scanner</span
              >
              Escanear QR
            </button>
          </form>
        </li>
      </ul>

      <div class="add-task-column">
        <input
          v-model="new_task"
          @keyup.enter.exact="addTask()"
          placeholder="Añadir otra zona..."
        />
        <button @click="addTask()" class="primary full-width">
          Agregar Zona
        </button>
        <div class="emergency-controls">
          <button @click="clearAll" class="danger full-width">
            Eliminar Emergencia
          </button>
        </div>
      </div>
    </div>
    <QrScanner
      v-if="showQrScanner"
      @person-scanned="addScannedPerson"
      @close="showQrScanner = false"
    />
  </div>
</template>

<style scoped>
.contabilidad-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #202225;
}

.contabilidad-header {
  padding: 20px 20px 20px 80px;
  background-color: #2f3136;
  border-bottom: 1px solid #40444b;
  flex-shrink: 0;
}

.contabilidad-header h2 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 24px;
}

.contabilidad-description {
  margin: 0;
  color: #b9bbbe;
  font-size: 14px;
}

.emergency-info {
  padding: 20px 24px;
  background-color: #2f3136;
  border-bottom: 1px solid #40444b;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.emergency-field {
  flex: 1;
  min-width: 300px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.emergency-label {
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}

.emergency-value {
  color: #dcddde;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex: 1;
  min-width: 200px;
}

.emergency-value:hover {
  background-color: #40444b;
}

.emergency-value.placeholder {
  color: #72767d;
  font-style: italic;
}

.emergency-input-inline {
  padding: 4px 8px;
  border: 1px solid #5865f2;
  border-radius: 4px;
  background-color: #202225;
  color: #dcddde;
  font-size: 14px;
  flex: 1;
  min-width: 200px;
  outline: none;
}

.emergency-input-inline::placeholder {
  color: #72767d;
}

.boards-container {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  overflow-x: auto;
  flex: 1;
  box-sizing: border-box;
}

/* Board/Column Styles */
.drop-zone {
  background-color: #2f3136; /* Darker column background */
  border-radius: 8px;
  padding: 8px;
  width: 300px;
  flex-shrink: 0;
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.board-header {
  font-size: 16px;
  font-weight: 600;
  padding: 8px;
  color: #ffffff;
}

.task-display {
  cursor: default;
}

.board-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.board-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.multi-select-btn {
  opacity: 0.7;
  transition: opacity 0.2s, background-color 0.2s;
}

.multi-select-btn.active {
  opacity: 1;
  background-color: #2dce89;
  color: white;
}

.multi-select-btn:hover {
  opacity: 1;
}

.board-edit-btn {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.board-edit-btn:hover {
  opacity: 1;
}

.multi-select-controls {
  padding: 8px;
  margin-top: 8px;
  font-size: 12px;
}

.move-selected-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.move-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.move-destination-btn {
  background-color: #4f545c;
  color: #dcddde;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  text-align: left;
}

.move-destination-btn:hover {
  background-color: #5c626b;
}

.board-header b {
  cursor: inherit;
  flex: 1;
}

.drag-el {
  background-color: #40444b; /* Lighter card background for better contrast */
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(9, 30, 66, 0.15);
  padding: 12px;
  list-style: none;
  cursor: grab;
  transition: box-shadow 0.2s ease, border 0.2s ease;
}

.drag-el:hover {
  box-shadow: 0 4px 8px rgba(9, 30, 66, 0.2);
}

.drag-el.multi-select-mode {
  cursor: pointer;
}

.drag-el.selected {
  border: 2px solid #2dce89;
  background-color: transparent;
}

.person-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.person-checkbox {
  width: 15px;
  height: 15px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: transparent;
  border: 2px solid #4f545c;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
}

.person-checkbox:hover {
  border-color: #2dce89;
}

.person-checkbox:checked {
  background-color: #2dce89;
  border-color: #2dce89;
}

.person-checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
  color: #ffffff;
}

.card-actions {
  display: flex;
  gap: 4px;
}

.person-function {
  font-size: 14px;
  color: #b9bbbe;
  margin: 0 0 8px 0;
  text-align: left;
}

input {
  width: 100%;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #202225;
  border-radius: 4px;
  background-color: #202225; /* Dark input background */
  box-sizing: border-box;
  transition: background-color 0.2s, border-color 0.2s;
  color: #dcddde;
}

input:focus {
  background-color: #36393f;
  border-color: #5865f2;
  outline: none;
}

.add-person-form,
.edit-person-form {
  margin-top: 8px;
}

.form-controls {
  display: flex;
  gap: 8px;
}

button {
  border-radius: 4px;
  border: none;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

button:active {
  transform: translateY(1px);
}

button.primary {
  background-color: #2dce89; /* Muted Teal */
  color: white;
}
button.primary:hover {
  background-color: #1d9863;
}

button.danger {
  background-color: #ed4245; /* Modern red */
  color: white;
}
button.danger:hover {
  background-color: #c43a3d;
}

button.secondary {
  background-color: #4f545c; /* Darker grey */
  color: #dcddde;
}
button.secondary:hover {
  background-color: #5c626b;
}

button.edit-btn {
  background: none;
  color: #b9bbbe;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
button.edit-btn:hover {
  background-color: #4f545c;
}

.material-icons {
  font-size: 20px;
}

button.full-width {
  width: 100%;
}

/* Add Task Column */
.add-task-column {
  width: 300px;
  flex-shrink: 0;
}

.emergency-controls {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #4f545c; /* Darker separator */
}

.add-task-column input {
  background-color: #2f3136;
  border: none;
  color: white;
  font-weight: 500;
}

.add-task-column input::placeholder {
  color: #b9bbbe;
}

.move-person-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.move-title {
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #ffffff;
}

.move-option {
  text-align: left;
}

.add-person-form {
  margin-top: 8px;
}
/* Responsive Styles for Mobile */
@media (max-width: 768px) {
  .contabilidad-header {
    padding: 16px 16px 16px 70px;
  }

  .contabilidad-header h2 {
    font-size: 20px;
  }

  .emergency-info {
    padding: 16px;
    flex-direction: column;
    gap: 16px;
  }

  .emergency-field {
    min-width: unset;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .emergency-value,
  .emergency-input-inline {
    min-width: unset;
    width: 100%;
  }

  .boards-container {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .drop-zone,
  .add-task-column {
    width: 100%;
    box-sizing: border-box; /* Ensure padding is included in the width */
    margin-bottom: 16px; /* Add space between stacked columns */
  }

  .add-task-column {
    margin-bottom: 0; /* Remove bottom margin from last column */
  }

  input {
    /* Prevent auto-zoom on mobile (iOS) by setting font-size to at least 16px */
    font-size: 16px;
  }
}
</style>
